import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import { colors, frames, glyphs, useMediaQuery } from "../../../../Styles";
import { PageHeader, CardNumberDisplay, GridLayoutLfOverview, WidgetModal, AllWidgets, Attentions, SystemControls, AutoReportWidget } from "../../../../Components";
import { WidgetPlacements } from "../../../../Definitions";
import { useAppSelector } from "../../../../Store";
import { Graph_QL, useSystem, useSection, useWidget, Widget } from "../../../../Services";

// #region | CardDisplay
const CardDisplay = memo(({ numberOfSections, numberOfPens, numberOfAnimals }: { numberOfSections: number; numberOfPens: number; numberOfAnimals: number; }) => {
  const { t } = useTranslation();
  return (
    <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      flexDirection: "row",
      gap: "10px",
      boxSizing: "border-box",
      alignItems: "center",
      width: "100%",
    }}
  >
    <CardNumberDisplay
      value={numberOfSections}
      unit={t("locationFeederPages.locationFeeder.sections", { defaultValue: "Sections" })}
      glyph={
        <glyphs.Animal fillColor={colors.greySecondary} size="150px" />
      }
    />
    <CardNumberDisplay
      value={numberOfPens}
      unit={t("locationFeederPages.locationFeeder.pens", { defaultValue: "Pens" })}
      glyph={
        <glyphs.Animal fillColor={colors.greySecondary} size="150px" />
      }
    />
    <CardNumberDisplay
      value={numberOfAnimals}
      unit={t("locationFeederPages.locationFeeder.animals", { defaultValue: "Animals" })}
      glyph={<glyphs.Animal fillColor={colors.greySecondary} size="150px" />}
    />
  </div>
  )
});
// #endregion

export default function FreedaLocationFeeder() {
  const { t } = useTranslation();
  const hideBtnText = useMediaQuery(`(max-width: 450px)`);
  const hideBtns = useMediaQuery(`(max-width: 600px)`);
  const [widgetModal, setWidgetModal] = useState<boolean>(false)

  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const location = useLocation();
  const systemId = useMemo(
    () => (urlSystemId ? +urlSystemId : location?.state?.selected),
    [urlSystemId, location]
  );

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm });
    let connection = Graph_QL.create({ domain: farm });
    return { domain, connection };
  }, [farm]);

  const { call: getSystemCall, data: systemsState } = useSystem(connection);
  const { call: getLfSectionsInfo, data: lfSectionsState } = useSection(connection);
  const { call: getWidgetInfo, data: widgetState } = useWidget(connection);
  // const { call: getAnimals, data: animalState } = useAnimal(connection);

  useEffect(() => {
    getSystemCall({ ids: [systemId]});
    getLfSectionsInfo({ ids: [systemId] }, connection);
    getWidgetInfo({ });
  }, []);

  const widgets = useMemo<Widget[]>(() => { return widgetState[domain]?.data ?? [] }, [widgetState, domain])
  
  // Get System Name for Header
  const system = useMemo(() => systemsState[domain]?.data.find(system => system.system_id === systemId), [domain, systemId, systemsState]);

  const animals = useMemo(() => {
    const systemSections = lfSectionsState[domain]?.data?.filter(
      section => section.system_id === systemId
    ) || [];
  
    // Flatten out all animals in all sections and pens
    const animals = systemSections.flatMap(section =>
      section.section_locations.flatMap(location =>
        location.animals || []
      )
    );
  
    return animals;
  }, [domain, lfSectionsState, systemId]);


  // Get System Section Data
  const { numberOfSections, numberOfPens, numberOfAnimals } = useMemo(() => {
    const sectionsData = lfSectionsState[domain]?.data.filter(s => s.system_id === systemId) || [];

    const sectionsCount = sectionsData.length;
    const pensCount =  sectionsData.reduce((accumulator, section) => {
      accumulator += section?.section_locations?.length
      return accumulator;
    }, 0)//(sectionsData[0]?.section_locations)?.length ?? 0;

    const animalsCount = sectionsData.reduce((accSections, section) => {
      const sectionAnimals = section.section_locations.reduce((accLocations, location) => {
        return accLocations + (location.animals?.length ?? 0);
      }, 0);
      return accSections + sectionAnimals;
    }, 0);

    return {
      numberOfSections: sectionsCount,
      numberOfPens: pensCount,
      numberOfAnimals: animalsCount,
    };
  }, [domain, lfSectionsState, systemId]);

  // SystemControls Component
  const SystemControlsComponent = useMemo(() => (
    <SystemControls systemId={systemId} key={systemId} />
  ), [systemId]);

  // Attentions Component
  const AttentionsComponent = useMemo(() => (
    <Attentions height={"100%"} connect={{connection: connection, domain: domain}}/>
  ), [connection, domain]);

  const AllWidgetsComp = useMemo(() => (
    <AllWidgets
      widgets={widgets}
      animals={animals}
      system={system}
      placement={WidgetPlacements.system}
      connect={{
        domain: domain,
        connection: connection
      }}
    />
    ), [animals, connection, domain, system, widgets])

  return (
    <frames.Page_inner>
      <PageHeader
        topHeader={system?.name}
        subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
        optionButtons={{
          contents: [t("widgets.general.widgetOptions", {defaultValue: "widget options"})],
          onClick: (i => {
            if (i === 0) { setWidgetModal(true) };
          })
        }}
      />

      <GridLayoutLfOverview
        CardDisplayComponent={<CardDisplay numberOfAnimals={numberOfAnimals} numberOfPens={numberOfPens} numberOfSections={numberOfSections}/>}
        SystemControlsComponent={SystemControlsComponent}
        AttentionsComponent={AttentionsComponent}
        WidgetComponents={ AllWidgetsComp}
      />

      <WidgetModal widgets={widgets} closeModal={() => setWidgetModal(false)} connect={{connection: connection, domain: domain}} isModalOpen={widgetModal} system={system} placement={WidgetPlacements.system}/>
    </frames.Page_inner>
  );
}